import React from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Radio, Network, Wrench, Building2, ShieldCheck, Timer, Lock,
  Globe, Plug, Zap, Satellite, ListChecks, FileText, Lightbulb, ArrowRight,
  Check, Server, Headset,
} from "lucide-react";
import Logo from "../components/Logo";
import Footer from "../components/Footer";
import PipelineCanvas from "../components/PipelineCanvas";
import { PARENT_URL } from "../utils/branding";

const USE_CASES = [
  { Icon: Headset, cat: "VOICE", catClass: "au-cat-amber", t: "Call Centers", d: "Verify SIP infrastructure for outbound and inbound campaigns. Ensure quality and uptime." },
  { Icon: Server, cat: "CARRIER", catClass: "au-cat-blue", t: "VoIP Providers", d: "Test customer SIP servers before onboarding. Diagnose connectivity issues quickly." },
  { Icon: Wrench, cat: "OPS", catClass: "au-cat-gray", t: "IT Administrators", d: "Troubleshoot PBX systems, verify firewall configurations, validate SRV records." },
  { Icon: Building2, cat: "SCALE", catClass: "au-cat-green", t: "Enterprises", d: "Monitor critical voice infrastructure. Validate redundancy and failover configurations." },
];

const FEATURES = [
  { Icon: Globe, cat: "DNS", catClass: "au-cat-blue", t: "DNS Resolution", d: "Verify hostname resolves correctly with full DNS lookup including SRV records for SIP discovery." },
  { Icon: Plug, cat: "PORTS", catClass: "au-cat-amber", t: "Port Availability", d: "Test UDP/TCP connectivity on 5060 (SIP) and 5061 (SIP-TLS) ports with response analysis." },
  { Icon: Zap, cat: "LATENCY", catClass: "au-cat-amber", t: "Latency Testing", d: "Measure round-trip time to SIP server. Identify network bottlenecks affecting call quality." },
  { Icon: Lock, cat: "SECURITY", catClass: "au-cat-green", t: "TLS Support", d: "Verify SIP-TLS encryption availability for secure VoIP communications on port 5061." },
  { Icon: Radio, cat: "PROTOCOL", catClass: "au-cat-blue", t: "SIP OPTIONS", d: "Send actual SIP OPTIONS request to verify protocol-level handshake and server responsiveness." },
  { Icon: Satellite, cat: "NAT", catClass: "au-cat-gray", t: "STUN Discovery", d: "Check STUN server reachability for NAT traversal in WebRTC and modern VoIP systems." },
  { Icon: ListChecks, cat: "RECORDS", catClass: "au-cat-blue", t: "DNS SRV Records", d: "Validate _sip._udp, _sip._tcp, _sips._tcp service records for proper SIP discovery." },
  { Icon: FileText, cat: "REPORTS", catClass: "au-cat-gray", t: "Detailed Reports", d: "Comprehensive results with severity-based issues, fix suggestions, and exportable data." },
  { Icon: Lightbulb, cat: "GUIDANCE", catClass: "au-cat-amber", t: "Diagnostic Tips", d: "Actionable recommendations to fix detected issues. From firewall rules to DNS configuration." },
];

const FLOW = [
  { Icon: Globe, t: "Resolve DNS", s: "Hostname & SRV lookup" },
  { Icon: Plug, t: "Port Check", s: "UDP/TCP 5060 / 5061" },
  { Icon: Lock, t: "TLS Handshake", s: "SIP-TLS on 5061" },
  { Icon: Radio, t: "SIP OPTIONS", s: "Protocol handshake" },
  { Icon: FileText, t: "Report", s: "Issues & fixes" },
];

export default function SipLanding() {
  const nav = useNavigate();

  return (
    <div className="au-root">
      {/* NAV */}
      <nav className="au-nav">
        <Link to="/" className="au-brand" aria-label="SIP Tester home">
          <Logo size="small" />
        </Link>
        <div className="au-navlinks">
          <a href={PARENT_URL}>GetKhojo</a>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <Link to="/about">About</Link>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="au-pill au-pill-ghost au-pill-sm" onClick={() => nav("/login")}>Login</button>
          <button className="au-pill au-pill-primary au-pill-sm" onClick={() => nav("/register")}>
            Start Free
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="au-section au-reveal">
        <div className="au-container au-center" style={{ maxWidth: 980 }}>
          <span className="au-chip au-chip-status" style={{ marginBottom: 22 }}>
            <Network size={14} /> PROFESSIONAL VOIP TESTING
          </span>
          <h1 style={{ fontSize: "clamp(2.6rem, 7vw, 5rem)", margin: "8px auto 18px", maxWidth: 820 }}>
            VoIP testing.<br />
            <span className="au-em">Made simple.</span>
          </h1>
          <p className="au-lead" style={{ maxWidth: 660, margin: "0 auto 34px" }}>
            Test SIP server reachability, DNS configuration, port availability, latency, TLS support — comprehensive VoIP infrastructure analysis in seconds.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="au-pill au-pill-primary" onClick={() => nav("/register")}>
              Start Free Test <ArrowRight size={18} />
            </button>
            <button
              className="au-pill au-pill-glass"
              onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
            >
              See Features
            </button>
          </div>

          {/* STAT ROW */}
          <div className="au-grid au-cols-4" style={{ maxWidth: 760, margin: "64px auto 0" }}>
            {[
              { n: "8", unit: "+", l: "Test Categories" },
              { n: "<30", unit: "s", l: "Fast Results" },
              { n: "UDP/TCP", unit: "", l: "Both Protocols" },
              { n: "TLS", unit: "", l: "Encryption" },
            ].map((s, i) => (
              <div key={i} className="au-center">
                <div className="au-stat-num">
                  {s.n}{s.unit && <span className="au-unit">{s.unit}</span>}
                </div>
                <div className="au-stat-lbl">{s.l}</div>
              </div>
            ))}
          </div>

          {/* SIGNATURE: SIP test-flow node canvas */}
          <div style={{ maxWidth: 1040, margin: "56px auto 0" }}>
            <PipelineCanvas
              ariaLabel="SIP test flow: Resolve DNS, Port Check, TLS Handshake, SIP OPTIONS, then Report"
              nodes={[
                { id: "dns",     x: 11, y: 30, icon: <Globe />,    title: "Resolve DNS",   sub: "Hostname & SRV" },
                { id: "port",    x: 33, y: 70, icon: <Plug />,     title: "Port Check",    sub: "UDP/TCP 5060/5061" },
                { id: "tls",     x: 50, y: 30, icon: <Lock />,     title: "TLS Handshake", sub: "SIP-TLS · 5061" },
                { id: "options", x: 70, y: 70, icon: <Radio />,    title: "SIP OPTIONS",   sub: "Protocol handshake" },
                { id: "report",  x: 89, y: 30, icon: <FileText />, title: "Report",        sub: "Issues & fixes" },
              ]}
              edges={[
                { from: "dns",     to: "port" },
                { from: "port",    to: "tls" },
                { from: "tls",     to: "options" },
                { from: "options", to: "report" },
              ]}
            />
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="au-section au-reveal">
        <div className="au-container">
          <div className="au-center" style={{ marginBottom: 56 }}>
            <span className="au-eyebrow">Who Uses SIP Tester</span>
            <h2 style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)", marginTop: 12 }}>
              Trusted by <span className="au-em">professionals.</span>
            </h2>
          </div>
          <div className="au-grid au-cols-4">
            {USE_CASES.map((u, i) => (
              <article key={i} className="au-glass au-pad-lg au-lift">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <span className="au-badge"><u.Icon /></span>
                  <span className={`au-chip au-chip-cat ${u.catClass}`}>{u.cat}</span>
                </div>
                <h3 style={{ fontSize: "1.15rem", marginBottom: 8 }}>{u.t}</h3>
                <p style={{ fontSize: ".88rem", color: "var(--au-text-2)", lineHeight: 1.6, margin: 0 }}>{u.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="au-section au-reveal">
        <div className="au-container">
          <div className="au-center" style={{ marginBottom: 56 }}>
            <span className="au-eyebrow">What We Test</span>
            <h2 style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)", marginTop: 12 }}>
              Complete SIP analysis. <span className="au-em">One test.</span>
            </h2>
          </div>
          <div className="au-grid au-cols-3">
            {FEATURES.map((f, i) => (
              <article key={i} className="au-glass au-pad-lg au-lift">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <span className="au-badge"><f.Icon /></span>
                  <span className={`au-chip au-chip-cat ${f.catClass}`}>{f.cat}</span>
                </div>
                <h3 style={{ fontSize: "1.1rem", marginBottom: 8 }}>{f.t}</h3>
                <p style={{ fontSize: ".88rem", color: "var(--au-text-2)", lineHeight: 1.6, margin: 0 }}>{f.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="au-section au-reveal">
        <div className="au-container">
          <div className="au-center" style={{ marginBottom: 56 }}>
            <span className="au-eyebrow">Simple Pricing</span>
            <h2 style={{ fontSize: "clamp(2rem, 4.5vw, 3rem)", marginTop: 12 }}>
              Start free. <span className="au-em">Scale when ready.</span>
            </h2>
          </div>
          <div className="au-grid au-cols-4">
            <SipPriceCard name="STARTER" price="₹0" period="forever" features={[["Y", "2 tests per day"], ["Y", "Basic reachability"], ["Y", "Port testing"], ["Y", "DNS resolution"], ["N", "Latency metrics"], ["N", "TLS analysis"], ["N", "SRV records"]]} cta="Start Free" ctaAction={() => nav("/register")} highlight={false} />
            <SipPriceCard name="BASIC" price="₹999" period="per month" features={[["Y", "30 tests per day"], ["Y", "Multi-Auth tests"], ["Y", "Latency metrics"], ["Y", "TLS verification"], ["Y", "Email reports"], ["N", "SRV records"], ["N", "API access"]]} cta="Get Started" ctaAction={() => nav("/register")} highlight={false} />
            <SipPriceCard name="PROFESSIONAL" price="₹4,999" period="per month" features={[["Y", "100 tests per day"], ["Y", "Full SIP analysis"], ["Y", "Latency metrics"], ["Y", "TLS verification"], ["Y", "SRV records check"], ["Y", "STUN testing"], ["Y", "Priority support"]]} cta="Upgrade Now" ctaAction={() => nav("/register")} highlight={true} badge="POPULAR" />
            <SipPriceCard name="ENTERPRISE" price="Custom" period="contact us" features={[["Y", "Unlimited tests"], ["Y", "Multiple servers"], ["Y", "API integration"], ["Y", "Custom integrations"], ["Y", "Dedicated support"], ["Y", "Custom SLA"], ["Y", "White-label option"]]} cta="Contact Sales" ctaAction={() => { window.location.href = "mailto:support@getkhojo.com"; }} highlight={false} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="au-section au-reveal">
        <div className="au-container">
          <div className="au-glass au-pad-lg au-center" style={{ maxWidth: 820, margin: "0 auto", padding: "56px 32px" }}>
            <span className="au-badge au-badge-dark" style={{ margin: "0 auto 22px" }}><ShieldCheck /></span>
            <h2 style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)", marginBottom: 14 }}>
              Ready to test your<br />
              <span className="au-em">SIP infrastructure?</span>
            </h2>
            <p className="au-lead" style={{ marginBottom: 30 }}>
              Free tier available. Test in under 30 seconds.
            </p>
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="au-pill au-pill-primary" onClick={() => nav("/register")}>
                Start Free Test <ArrowRight size={18} />
              </button>
              <span className="au-chip au-chip-status">
                <Timer size={14} /> Results in &lt;30s
              </span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SipPriceCard({ name, price, period, features, cta, ctaAction, highlight, badge }) {
  return (
    <div
      className={`au-glass au-pad-lg${highlight ? "" : " au-lift"}`}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        ...(highlight ? { border: "1.5px solid var(--au-accent)", boxShadow: "var(--au-shadow-float), var(--au-glow)" } : {}),
      }}
    >
      {badge && (
        <span
          className="au-chip au-chip-cat au-cat-amber"
          style={{ position: "absolute", top: -12, right: 22 }}
        >
          {badge}
        </span>
      )}
      <div className="au-eyebrow" style={{ marginBottom: 12 }}>{name}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <span className="au-stat-num" style={{ fontSize: "2.2rem" }}>{price}</span>
        <span style={{ fontSize: ".78rem", color: "var(--au-muted)" }}>{period}</span>
      </div>
      <div style={{ height: 1, background: "var(--au-hair)", margin: "18px 0" }} />
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 24px", display: "flex", flexDirection: "column", gap: 10, minHeight: 220, flex: 1 }}>
        {features.map(([yes, label], i) => (
          <li
            key={i}
            style={{
              display: "flex", alignItems: "center", gap: 8, fontSize: ".85rem", fontWeight: 500,
              color: yes === "Y" ? "var(--au-text-2)" : "var(--au-muted)",
            }}
          >
            {yes === "Y"
              ? <Check size={15} style={{ color: "var(--au-accent-ink)", flex: "none" }} aria-hidden="true" />
              : <Lock size={13} style={{ color: "var(--au-muted)", flex: "none" }} aria-hidden="true" />}
            <span style={{ opacity: yes === "Y" ? 1 : 0.7 }}>{label}</span>
          </li>
        ))}
      </ul>
      <button
        className={`au-pill ${highlight ? "au-pill-primary" : "au-pill-glass"}`}
        style={{ width: "100%" }}
        onClick={ctaAction}
      >
        {cta}
      </button>
    </div>
  );
}
